import {Injectable} from '@angular/core';
import {LookupService} from './lookup.service';
import {PaperSizeLookup} from '../models/paper-size.model';
import {PlanReferenceScaleLookup, ReferenceScaleLookup} from '../models/reference-scale.model';
import {SpatialUtilService} from '../../design/services/spatial-util.service';

@Injectable({
    providedIn: 'root'
})
export class PrintAreaService {
    title = 'PrintAreaService';

    paperSizeLookups: PaperSizeLookup[];

    referenceScaleLookups: PlanReferenceScaleLookup[];

    referenceScaleLookup: PlanReferenceScaleLookup;

    printAreaFeatureLayer: object;

    constructor(
        private lookupService: LookupService,
        private spatialUtilService: SpatialUtilService
    ) {}

    getPrintAreaFeature() {
        return this.spatialUtilService.getPrintAreas()[0];
    }

    getPrintAreaGeometry() {
        const printAreaFeature = this.getPrintAreaFeature();
        return printAreaFeature && printAreaFeature.geometry;
    }

    getPaperSizeLookups() {
        if (!this.paperSizeLookups) {
            // NOTE: The id in PaperSizeLookup.java is a string so must be parsed to an int
            this.paperSizeLookups = this.lookupService
                .getLookups('paperSize')
                .map((l: PaperSizeLookup) => Object.assign(l, {id: parseInt(`${l.id}`)}));
        }
        return this.paperSizeLookups;
    }

    getPaperSizeLookup() {
        const printAreaFeature = this.getPrintAreaFeature();
        if (printAreaFeature) {
            return this.getPaperSizeLookups().find(l => l.id === printAreaFeature.attributes.papersize);
        }
        return this.getPaperSizeLookups().find(l => l.defaultOption);
    }

    getPaperSize() {
        return this.getPaperSizeLookup().paperSize;
    }

    getPaperSizeLookupId() {
        return this.getPaperSizeLookup().id;
    }

    getReferenceScaleLookups() {
        if (!this.referenceScaleLookups) {
            this.referenceScaleLookups = this.lookupService
                .getLookups('referenceScale')
                .sort((l1: ReferenceScaleLookup, l2: ReferenceScaleLookup) => (l1.uiOptionOrder > l2.uiOptionOrder ? 1 : -1))
                .filter((l: ReferenceScaleLookup) => l.plotPrinting)
                .map((l: ReferenceScaleLookup) =>
                    Object.assign({} as PlanReferenceScaleLookup, l, {id: parseInt(l.id), divisor: parseInt(l.referenceScale.split(':')[1])})
                );
        }
        return this.referenceScaleLookups;
    }

    getReferenceScaleLookup() {
        const printAreaFeature = this.getPrintAreaFeature();
        if (printAreaFeature) {
            return printAreaFeature && this.getReferenceScaleLookups().find(l => l.id === printAreaFeature.attributes.scale);
        } else if (this.referenceScaleLookup) {
            return this.referenceScaleLookup;
        } else {
            return this.getDefaultReferenceScale();
        }
    }

    setReferenceScaleLookup(referenceScaleLookup: PlanReferenceScaleLookup) {
        this.referenceScaleLookup = referenceScaleLookup;
    }

    getDefaultReferenceScale() {
        return this.getReferenceScaleLookups().find((l: PlanReferenceScaleLookup) => l.divisor === 750);
    }

    isPrintAreaReferenceScaleIdValid() {
        const printAreaFeature = this.getPrintAreaFeature();
        return printAreaFeature && this.isReferenceScaleIdValid(printAreaFeature.attributes.scale);
    }

    isReferenceScaleIdValid(scaleId: number) {
        return this.getReferenceScaleLookups().filter(l => l.id === scaleId).length > 0;
    }

    getPrintAreaScaleId() {
        const printAreaFeature = this.getPrintAreaFeature();
        return printAreaFeature && printAreaFeature.attributes.scale;
    }

    getReferenceScaleLookupId() {
        return this.getReferenceScaleLookup().id;
    }

    getReferenceScale() {
        return this.getReferenceScaleLookup().referenceScale;
    }

    getReferenceScaleDivisor() {
        return this.getReferenceScaleLookup().divisor;
    }

    init(printAreaFeatureLayer: object) {
        this.printAreaFeatureLayer = printAreaFeatureLayer;
    }
}
